/**
 * API Configuration and HTTP Client Setup
 * Following constitution requirements for API integration
 *
 * This file configures the HTTP client and defines all API endpoints
 * used throughout the ArbiWin application.
 */

import axios from 'axios';
import { getEnvironmentConfig } from './environment';
import { convertBookmakerIdsToApiFormat } from '../utils/bookmakerUtils';

/**
 * API Endpoints Configuration
 * Following the exact backend API structure from constitution
 */
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    REFRESH_TOKEN: '/api/auth/refresh',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    VERIFY_EMAIL: '/api/auth/verify-email'
  },

  // Subscription management endpoints
  SUBSCRIPTION: {
    STATUS: '/api/subscriptions/status',
    ACTIVATE: '/api/subscriptions/activate',
    DEACTIVATE: '/api/subscriptions/deactivate',
    HISTORY: '/api/subscriptions/history',
    PLANS: '/api/subscriptions/plans'
  },

  // Core surebet functionality (following constitution)
  SUREBET: {
    MARKETS_ANALYSIS: '/api/core/markets-analysis',
    BOOKMAKERS: '/api/core/bookmakers',
    SPORTS: '/api/core/sports',
    CHAMPIONSHIPS: '/api/core/championships'
  },

  // User management
  USER: {
    PROFILE: '/api/users/profile',
    UPDATE_PROFILE: '/api/users/profile',
    PREFERENCES: '/api/users/preferences',
    DELETE_ACCOUNT: '/api/users/delete'
  },

  // Admin endpoints
  ADMIN: {
    USERS: '/api/admin/users',
    SUBSCRIPTIONS: '/api/admin/subscriptions',
    ANALYTICS: '/api/admin/analytics',
    ACTIVITY_SUMMARY: '/api/admin/activity/summary',
    SYSTEM_STATUS: '/api/admin/system-status'
  }
};

/**
 * HTTP Status Codes for consistent error handling
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402, // Subscription required
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
};

/**
 * Create configured Axios instance
 * @returns {Object} Configured Axios instance
 */
export function createApiClient() {
  const envConfig = getEnvironmentConfig();

  const apiClient = axios.create({
    baseURL: envConfig.api.baseURL,
    timeout: envConfig.api.timeout,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // Request interceptor for authentication
  apiClient.interceptors.request.use(
    function(config) {
      // Add JWT token if available
      const token = localStorage.getItem('arbiwin_auth_token');
      if (token) {
        config.headers.Authorization = 'Bearer ' + token;
      }

      // Add CSRF token if available
      const csrfToken = document.querySelector('meta[name="csrf-token"]');
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken.getAttribute('content');
      }

      // Log request in development
      if (envConfig.debug.enabled) {
        console.log('API Request:', {
          method: config.method.toUpperCase(),
          url: config.url,
          data: config.data,
          params: config.params
        });
      }

      return config;
    },
    function(error) {
      if (envConfig.debug.enabled) {
        console.error('API Request Error:', error);
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  apiClient.interceptors.response.use(
    function(response) {
      // Log response in development
      if (envConfig.debug.enabled) {
        console.log('API Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data
        });
      }

      return response.data;
    },
    function(error) {
      // Handle specific HTTP status codes
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        // Log error in development
        if (envConfig.debug.enabled) {
          console.error('API Error Response:', {
            status: status,
            url: error.config.url,
            data: data,
            message: error.message
          });
        }

        // Handle subscription required (HTTP 402)
        if (status === HTTP_STATUS.PAYMENT_REQUIRED) {
          // Check if this is an authentication endpoint
          const endpoint = error.config?.url;
          const authEndpoints = [
            '/auth/login',
            '/auth/register',
            '/auth/forgot-password',
            '/auth/reset-password',
            '/auth/logout',
            '/auth/refresh-token',
            '/auth/validate-session'
          ];
          
          const isAuthEndpoint = authEndpoints.some(authEndpoint => 
            endpoint && endpoint.includes(authEndpoint)
          );
          
          // Only dispatch subscription required event for non-auth endpoints
          if (!isAuthEndpoint) {
            // Dispatch subscription required event
            window.dispatchEvent(new CustomEvent('subscription:required', {
              detail: { error: error, endpoint: error.config.url }
            }));
          }
        }

        // Handle authentication errors (HTTP 401)
        if (status === HTTP_STATUS.UNAUTHORIZED) {
          // Clear invalid token
          localStorage.removeItem('arbiwin_auth_token');

          // Dispatch authentication required event
          window.dispatchEvent(new CustomEvent('auth:required', {
            detail: { error: error, endpoint: error.config.url }
          }));
        }

        // Create standardized error object
        const apiError = {
          status: status,
          message: data && data.message || error.message,
          data: data,
          url: error.config.url,
          method: error.config.method
        };

        return Promise.reject(apiError);
      }

      // Network error
      if (error.request) {
        const networkError = {
          status: 0,
          message: 'Network error. Please check your connection.',
          data: null,
          url: error.config ? error.config.url : null,
          method: error.config ? error.config.method : null
        };

        return Promise.reject(networkError);
      }

      // Request setup error
      return Promise.reject({
        status: 0,
        message: error.message,
        data: null,
        url: null,
        method: null
      });
    }
  );

  return apiClient;
}

/**
 * API Client instance (singleton)
 */
export const apiClient = createApiClient();

/**
 * API Request helpers
 */
export const apiHelpers = {
  /**
   * Build query parameters for surebet filtering
   * @param {Object} filters - Filter object
   * @returns {Object} Query parameters
   */
  buildSurebetFilters: function(filters) {
    const params = {};

    if (filters.bookmakers && filters.bookmakers.length > 0) {
      // Convert bookmaker IDs to API format (e.g., bg8888 -> bg_8888)
      const apiFormattedBookmakers = convertBookmakerIdsToApiFormat(filters.bookmakers);
      params.bookmakers = apiFormattedBookmakers.join(',');
    }

    if (filters.sports && filters.sports.length > 0) {
      params.sports = filters.sports.join(',');
    }

    if (filters.championships && filters.championships.length > 0) {
      params.championships = filters.championships.join(',');
    }

    if (filters.minMargin && filters.minMargin > 0) {
      params.minMargin = filters.minMargin;
    }

    if (filters.maxMargin && filters.maxMargin > 0) {
      params.maxMargin = filters.maxMargin;
    }

    if (filters.dateFrom) {
      params.dateFrom = filters.dateFrom;
    }

    if (filters.dateTo) {
      params.dateTo = filters.dateTo;
    }

    return params;
  },

  /**
   * Build pagination parameters
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @returns {Object} Pagination parameters
   */
  buildPaginationParams: function(page, size) {
    return {
      page: page || 0,
      size: size || 20
    };
  },

  /**
   * Handle API response with error checking
   * @param {Promise} apiCall - API call promise
   * @returns {Promise} Handled response
   */
  handleResponse: function(apiCall) {
    return apiCall
      .then(function(response) {
        return { data: response, error: null };
      })
      .catch(function(error) {
        return { data: null, error: error };
      });
  }
};

/**
 * API Service base class for consistent service implementation
 */
export function createApiService(baseEndpoint) {
  return {
    /**
     * GET request
     * @param {string} endpoint - Endpoint path
     * @param {Object} params - Query parameters
     * @returns {Promise} API response
     */
    get: function(endpoint, params) {
      const url = baseEndpoint + (endpoint || '');
      return apiHelpers.handleResponse(apiClient.get(url, { params: params }));
    },

    /**
     * POST request
     * @param {string} endpoint - Endpoint path
     * @param {Object} data - Request body
     * @returns {Promise} API response
     */
    post: function(endpoint, data) {
      const url = baseEndpoint + (endpoint || '');
      return apiHelpers.handleResponse(apiClient.post(url, data));
    },

    /**
     * PUT request
     * @param {string} endpoint - Endpoint path
     * @param {Object} data - Request body
     * @returns {Promise} API response
     */
    put: function(endpoint, data) {
      const url = baseEndpoint + (endpoint || '');
      return apiHelpers.handleResponse(apiClient.put(url, data));
    },

    /**
     * DELETE request
     * @param {string} endpoint - Endpoint path
     * @returns {Promise} API response
     */
    delete: function(endpoint) {
      const url = baseEndpoint + (endpoint || '');
      return apiHelpers.handleResponse(apiClient.delete(url));
    }
  };
}

export default apiClient;
